import { QueryClient } from "@tanstack/react-query";
import { isApiProblem } from "./problem.js";

/**
 * How long a computed report or deal statement is served from the cache
 * before a screen that mounts it asks again.
 *
 * Every `/reports/*` answer and every statement is worked out from the deals'
 * term lines on each request, and the open month's statements are the slowest
 * thing the API does. Going back to a page a minute later should not pay for
 * that again. A write made from this SPA still restates them at once: every
 * mutation invalidates `["report"]` and `["deal-statement"]` (mutations.ts),
 * which refetches whatever is on screen whatever this says. What is left is a
 * change somebody ELSE made, which a 2-5 person tool can wait five minutes for.
 */
export const REPORT_STALE_TIME_MS = 5 * 60_000;

/** The query-key roots `REPORT_STALE_TIME_MS` and `reportRetry` apply to. */
export const COMPUTED_QUERY_ROOTS = [["report"], ["deal-statement"]] as const;

/**
 * The general rule (below) and one more refusal: a report that timed out at the
 * gateway (502/503/504) is not asked again by itself. The server is very likely
 * still working the first request out, and a second one only doubles the work
 * that made it slow. The error screen offers a retry to the person instead.
 */
export function reportRetry(failureCount: number, error: unknown): boolean {
  if (isApiProblem(error) && !error.transport && [502, 503, 504].includes(error.status)) {
    return false;
  }
  return defaultRetry(failureCount, error);
}

/**
 * The retry rule is the interesting part. A 4xx from this API is a statement
 * about the REQUEST — an unknown filter, a bad sort field, a missing record,
 * each with a stable code — and retrying it produces the same failure three
 * times while the screen sits on a spinner instead of showing the reason.
 * Transport failures and 5xx are worth one retry.
 */
function defaultRetry(failureCount: number, error: unknown): boolean {
  if (isApiProblem(error) && !error.transport && error.status < 500) return false;
  return failureCount < 1;
}

/**
 * A factory, not a shared instance: tests need a client whose cache doesn't
 * outlive them.
 */
export function createQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        retry: defaultRetry,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
    },
  });
  for (const root of COMPUTED_QUERY_ROOTS) {
    client.setQueryDefaults(root, { staleTime: REPORT_STALE_TIME_MS, retry: reportRetry });
  }
  return client;
}
